import React from 'react'
import { Box, Typography, Chip, Tooltip, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import BadgeIcon from '@mui/icons-material/Badge'
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import { IKycProfile, KYC_PROFILES, KycDocumentType } from '@/api/services/DeveloperService'
import { subtleLabelSx } from '../styles'

// ── Helpers ────────────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<KycDocumentType, string> = {
  licence: 'Licence',
  passport: 'Passport',
  medicare: 'Medicare',
}

// Brand secondary palette used only in the detail panel header
const DOC_TYPE_HEADER: Record<KycDocumentType, { bg: string; text: string }> = {
  licence: { bg: '#ebefff', text: '#2c063f' },
  passport: { bg: '#eef9fc', text: '#2c063f' },
  medicare: { bg: '#d9f2ec', text: '#2c063f' },
}

function DocIcon({ type, size = 14 }: { type: KycDocumentType; size?: number }) {
  const sx = { fontSize: size }
  if (type === 'licence') return <BadgeIcon sx={sx} />
  if (type === 'passport') return <AirplaneTicketIcon sx={sx} />
  return <LocalHospitalIcon sx={sx} />
}

function formatAddress(p: IKycProfile): string {
  const { unitNumber, streetNumber, streetName, suburb, state, postcode } = p.address
  const street = [unitNumber && `Unit ${unitNumber}`, `${streetNumber} ${streetName}`]
    .filter(Boolean)
    .join(', ')
  return `${street}, ${suburb} ${state} ${postcode}`
}

// ── Detail panel ───────────────────────────────────────────────────────────────

function ProfileDetail({ profile }: { profile: IKycProfile }) {
  const header = DOC_TYPE_HEADER[profile.documentType]

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Document number', value: profile.documentNumber },
    ...(profile.secondaryNumber
      ? [{ label: 'Secondary number', value: profile.secondaryNumber }]
      : []),
    ...(profile.expiry
      ? [
          {
            label: profile.documentType === 'medicare' ? 'Validity' : 'Expiry',
            value: profile.expiry,
          },
        ]
      : []),
    ...(profile.positionOnCard != null
      ? [{ label: 'Position on card', value: String(profile.positionOnCard) }]
      : []),
    { label: 'Full name', value: `${profile.givenName} ${profile.middleName} ${profile.surname}` },
    { label: 'Date of birth', value: profile.dateOfBirth },
    { label: 'Address', value: formatAddress(profile) },
  ]

  return (
    <Box sx={{ mt: 1.5, border: '1px solid #e0d7e4', borderRadius: 2, overflow: 'hidden' }}>
      {/* Header bar */}
      <Box
        sx={{
          backgroundColor: header.bg,
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          borderBottom: '1px solid #e0d7e4',
        }}
      >
        <DocIcon type={profile.documentType} />
        <Typography variant="caption" fontWeight={700} color={header.text}>
          {DOC_TYPE_LABELS[profile.documentType]} — {profile.givenName} {profile.surname}
        </Typography>
      </Box>

      {/* Detail rows */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          backgroundColor: '#f5f2f7',
        }}
      >
        {rows.map(({ label, value }) => (
          <Box key={label} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ color: '#a08fa8', minWidth: 130, flexShrink: 0 }}>
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#2c063f', fontFamily: 'monospace', fontSize: 11 }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  selectedProfileId: string | null
  onSelect: (profile: IKycProfile | null) => void
}

export const KycProfileSelector: React.FC<Props> = ({ selectedProfileId, onSelect }) => {
  const selectedProfile = KYC_PROFILES.find(p => p.id === selectedProfileId) ?? null

  return (
    <Box>
      {/* Label row */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}
      >
        <Typography variant="caption" sx={subtleLabelSx}>
          KYC profile
        </Typography>
        {selectedProfile && (
          <Tooltip title="Clear KYC profile">
            <IconButton
              size="small"
              aria-label="Clear KYC profile"
              onClick={() => onSelect(null)}
              sx={{ color: '#a08fa8', p: 0.25 }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Chips — selected state matches scenario template chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {KYC_PROFILES.map(profile => {
          const isSelected = selectedProfileId === profile.id
          return (
            <Tooltip
              key={profile.id}
              title={`${DOC_TYPE_LABELS[profile.documentType]} · ${profile.documentNumber}${profile.secondaryNumber ? ` / ${profile.secondaryNumber}` : ''}`}
              arrow
            >
              <Chip
                icon={<DocIcon type={profile.documentType} size={13} />}
                label={`${profile.givenName} ${profile.surname}`}
                size="small"
                onClick={() => onSelect(isSelected ? null : profile)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  backgroundColor: isSelected ? '#fa624b' : 'transparent',
                  color: isSelected ? '#2c063f' : '#4b2b5c',
                  borderColor: isSelected ? '#fa624b' : '#cac0cf',
                  border: '1px solid',
                  '& .MuiChip-icon': { color: isSelected ? '#2c063f' : '#a08fa8', ml: '6px' },
                  '&:hover': { backgroundColor: isSelected ? '#e5432e' : '#f5f2f7' },
                }}
              />
            </Tooltip>
          )
        })}
      </Box>

      {/* Expanded detail panel */}
      {selectedProfile && <ProfileDetail profile={selectedProfile} />}
    </Box>
  )
}
